import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getErrorMessage } from '../api/client';
import { applyCount, getCountTarget, previewCount } from '../api/inventoryCounts';
import type { CountPreview, CountResult, CountTarget } from '../types/inventoryCount';
import type { InventoryBalance } from '../types/inventory';
import { randomUuid } from '../utils/randomUuid';
import {
  buildCountPayload, countCompareState, countErrorNeedsRecompare, countSumEcho, countTotal, describeCountDifference, emptyCountDraft, pendingOldestText, sinceText, type CountDraft,
} from '../utils/inventoryCount';
import { formatWasteQuantity } from '../utils/wasteReview';
import { Answers, FooterReason, Question } from './capture';
import { Modal } from './Modal';
import { CheckRow } from './ui/CheckRow';
import { useConfirm } from './ui/ConfirmDialog';

// F3 · «Contar» un insumo en una tienda (OWNER/MANAGER). Primero se escribe lo contado —suelto y/o
// por empaques, la pantalla solo SUMA— y DESPUÉS se ve la diferencia en palabras. Nadie resta. El
// servidor calcula la diferencia, revisa ventas pendientes (D5) y aplica con su propia hora.

type Step = 'COUNT' | 'COMPARE' | 'DONE';

/** Tras mostrar la diferencia, «Confirmar» ignora toques durante este tiempo: un doble toque en «Ver diferencia» no confirma a ciegas. */
const CONFIRM_GUARD_MS = 600;

const errorCode = (error: unknown) => (error instanceof ApiError ? (error.payload as { error?: string } | undefined)?.error : undefined);

export function InventoryCountModal({ balance, onClose, onSaved }: {
  balance: Pick<InventoryBalance, 'storeId' | 'storeName' | 'ingredientId' | 'ingredientName'>;
  /** `maybeChanged` = se intentó registrar (aunque el resultado quedara en duda): hay que recargar existencias. */
  onClose: (maybeChanged: boolean) => void;
  onSaved: (result: CountResult) => void;
}) {
  const confirm = useConfirm();
  const [target, setTarget] = useState<CountTarget>();
  const [loadError, setLoadError] = useState<string>();
  const [step, setStep] = useState<Step>('COUNT');
  const [draft, setDraft] = useState<CountDraft>(emptyCountDraft);
  const [preview, setPreview] = useState<CountPreview>();
  const [wasteText, setWasteText] = useState('');
  const [reason, setReason] = useState('');
  const [syncAcknowledged, setSyncAcknowledged] = useState(false);
  const [supplyAcknowledged, setSupplyAcknowledged] = useState(false);
  // Una llave por COMPARACIÓN: un reintento idéntico (doble toque, red lenta, resultado en duda) usa la
  // misma y el servidor no ajusta dos veces. Cada comparación nueva trae una llave nueva, y el servidor
  // igual rechaza si la existencia ya no es la que se vio.
  const [requestId, setRequestId] = useState(() => randomUuid());
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<CountResult>();
  const comparedAt = useRef(0);
  const attempted = useRef(false);

  const loadTarget = useCallback(async () => {
    setLoadError(undefined);
    try {
      setTarget(await getCountTarget(balance.storeId, balance.ingredientId));
    } catch (targetError) {
      setLoadError(getErrorMessage(targetError, 'No fue posible abrir el conteo.'));
    }
  }, [balance.storeId, balance.ingredientId]);

  useEffect(() => { void loadTarget(); }, [loadTarget]);

  const presentations = target?.presentations ?? [];
  const unit = target?.ingredient.unit;
  const counted = countTotal(draft, presentations);
  const compare = countCompareState(preview, { wasteText, reason, syncAcknowledged, supplyAcknowledged });
  const close = () => { if (!isBusy) onClose(attempted.current); };

  const runPreview = async (countedQuantity: number) => {
    setIsBusy(true);
    setError(undefined);
    try {
      const next = await previewCount({ storeId: balance.storeId, ingredientId: balance.ingredientId, countedQuantity });
      setPreview(next);
      // Lo escrito para otra comparación no debe sobrevivir si ya no aplica.
      if (next.direction !== 'SHORTAGE') setWasteText('');
      setRequestId(randomUuid());
      comparedAt.current = Date.now();
      setStep('COMPARE');
      return true;
    } catch (previewError) {
      setError(getErrorMessage(previewError, 'No fue posible comparar con el sistema.'));
      // Si falla una comparación nueva (p. ej. tras «la existencia cambió»), la vieja no debe quedar
      // en pantalla con su llave: se vuelve a lo contado, que se conserva.
      setStep('COUNT');
      return false;
    } finally {
      setIsBusy(false);
    }
  };

  const goCompare = async () => {
    if (isBusy || counted.total === null) return;
    if (counted.total === 0 && !(await confirm({ title: '¿Contaste 0?', description: `Se registrará que no hay ${balance.ingredientName} en ${balance.storeName}.`, confirmLabel: 'Sí, no hay nada', cancelLabel: 'Revisar' }))) return;
    await runPreview(counted.total);
  };

  const submit = async () => {
    if (isBusy || !preview || compare.blocker || counted.total === null) return;
    if (Date.now() - comparedAt.current < CONFIRM_GUARD_MS) return;
    // Decisión del dueño: una diferencia grande pide una segunda confirmación, además del motivo.
    if (compare.needsReason) {
      const difference = describeCountDifference(preview);
      if (!(await confirm({ title: 'La diferencia es grande', description: `${difference.headline} ${difference.detail} ¿Registrar el conteo?`, confirmLabel: 'Sí, registrar', cancelLabel: 'Revisar' }))) return;
    }
    setIsBusy(true);
    setError(undefined);
    attempted.current = true;
    try {
      const saved = await applyCount(buildCountPayload({
        requestId,
        storeId: balance.storeId,
        ingredientId: balance.ingredientId,
        countedQuantity: counted.total,
        seenQuantity: preview.systemQuantity,
        detectedWasteQuantity: compare.waste.quantity,
        largeDifferenceReason: reason,
        large: compare.needsReason,
        staleSalesSyncAcknowledged: syncAcknowledged,
        pendingSupplyAcknowledged: supplyAcknowledged,
      }));
      setResult(saved);
      setStep('DONE');
      onSaved(saved);
    } catch (saveError) {
      const code = errorCode(saveError);
      const message = getErrorMessage(saveError, 'No fue posible registrar el conteo.');
      if (countErrorNeedsRecompare(code)) {
        // Nada se escribió: se vuelve a comparar conservando lo escrito, y la pantalla muestra lo que
        // ahora hace falta (existencia nueva, motivo, aviso de Loyverse, ventas pendientes, costo).
        setIsBusy(false);
        await runPreview(counted.total);
        setError(message);
        return;
      }
      if (saveError instanceof ApiError && saveError.status >= 400 && saveError.status < 500) {
        setError(message);
      } else {
        // Sin conexión, 5xx o tiempo agotado: no se sabe si se guardó. Reintentar con la misma llave no
        // duplica; y como un conteo fija la cantidad, volver a comparar mostraría «Cuadra».
        setError('No sabemos si se guardó: se perdió la conexión o el servidor no respondió. Toca «Confirmar conteo» otra vez; si ya se había guardado, no se duplica.');
      }
    } finally {
      setIsBusy(false);
    }
  };

  let body: JSX.Element;
  if (loadError) {
    body = <div className="alert alert--error" role="alert">{loadError}</div>;
  } else if (!target || !unit) {
    body = <div className="inline-loading"><span className="spinner" />Revisando el insumo…</div>;
  } else if (!target.storeStarted) {
    body = <div className="alert alert--warning" role="status"><strong>Carga primero el inventario inicial de esta tienda.</strong> Después ya podrás contar normal. Así las ventas viejas nunca se mezclan con un conteo.</div>;
  } else if (target.parallelBalance) {
    body = <div className="alert alert--warning" role="status">Este insumo tiene existencia registrada en otra unidad en esta tienda. Pide que se corrija antes de contar.</div>;
  } else if (step === 'COUNT' && target.pending?.blocked) {
    body = <>
      <div className="alert alert--warning" role="status">{target.pending.message} {pendingOldestText(target.pending.loyverseOldestAt)}</div>
      <button className="link-button" onClick={() => void loadTarget()} type="button">Ya lo resolví, revisar otra vez</button>
    </>;
  } else if (step === 'DONE' && result) {
    body = <DoneMessage result={result} unitSymbol={unit.symbol} />;
  } else if (step === 'COMPARE' && preview) {
    const difference = describeCountDifference(preview);
    body = <>
      <div className={`alert ${difference.tone === 'ok' ? 'alert--success' : difference.tone === 'bad' ? 'alert--error' : 'alert--warning'}`} role="status">
        <strong>{difference.headline}</strong> {difference.detail}
        {preview.direction === 'MATCH' && attempted.current && ' Si tu envío anterior sí se guardó, ya quedó registrado.'}
      </div>
      {compare.blockedMessage && <div className="alert alert--warning" role="status">{compare.blockedMessage}</div>}
      {compare.cannotApplySurplus && <div className="alert alert--warning" role="status">Sobra producto, pero este insumo no tiene costo registrado en esta tienda. Registra primero una compra o una entrada con costo.</div>}
      {preview.direction === 'SHORTAGE' && !compare.blockedMessage && <>
        <Question>¿Parte de lo que falta fue merma?</Question>
        <Answers>
          <label className="field">
            <span>Merma detectada en conteo, en {unit.symbol} (opcional, hasta {formatWasteQuantity(String(compare.shortage), unit.symbol)})</span>
            <input inputMode="decimal" maxLength={14} onChange={(event) => { setWasteText(event.target.value); setError(undefined); }} placeholder="0" type="text" value={wasteText} />
          </label>
          <p className="wiz-hint">Se registra como merma «Detectada en conteo». El resto queda como diferencia de conteo.</p>
        </Answers>
      </>}
      {compare.needsReason && <>
        <Question>La diferencia es grande: ¿por qué?</Question>
        <Answers>
          <label className="field">
            <span>Motivo (obligatorio, al menos 10 caracteres)</span>
            <textarea maxLength={300} onChange={(event) => { setReason(event.target.value); setError(undefined); }} placeholder="Ej. Se cayó una caja completa" rows={2} value={reason} />
          </label>
        </Answers>
      </>}
      {/* P-076: renglón de selección (la casilla junto al texto completo, que no se acorta: es un reconocimiento
          sobre inventario). */}
      {compare.needsSyncAck && (
        <CheckRow checked={syncAcknowledged} className="count-sync-ack" label={<>Última sincronización de Loyverse: {sinceText(preview.salesSync.lastSyncedAt)}. Puede haber ventas que todavía no llegan y se descontarían dos veces. Entiendo, contar de todos modos.</>} onChange={setSyncAcknowledged} />
      )}
      {compare.needsSupplyAck && preview.pendingSupply && (
        <CheckRow checked={supplyAcknowledged} className="count-sync-ack" label={`${preview.pendingSupply.message} Entiendo, contar de todos modos.`} onChange={setSupplyAcknowledged} />
      )}
    </>;
  } else {
    const hasPresentations = presentations.length > 0;
    const echo = countSumEcho(draft, presentations, unit.symbol);
    const updateLine = (index: number, changes: Partial<CountDraft['lines'][number]>) => setDraft({ ...draft, lines: draft.lines.map((other, at) => (at === index ? { ...other, ...changes } : other)) });
    body = <>
      {target.salesSync.stale && <div className="alert alert--warning" role="status">Última sincronización de Loyverse: {sinceText(target.salesSync.lastSyncedAt)}. Puede haber ventas que todavía no llegan; antes de registrar te pediremos confirmar.</div>}
      {target.pendingSupply && <div className="alert alert--warning" role="status">{target.pendingSupply.message}</div>}
      <Question>¿Cuántos {unit.symbol} de {balance.ingredientName} contaste?</Question>
      <Answers>
        <label className="field wiz-big-input">
          <span>{hasPresentations ? `Sueltos, en ${unit.name} (${unit.symbol})` : `Cantidad en ${unit.name} (${unit.symbol})`}</span>
          <input autoFocus enterKeyHint="done" inputMode="decimal" maxLength={14} onChange={(event) => { setDraft({ ...draft, loose: event.target.value }); setError(undefined); }} placeholder="0" type="text" value={draft.loose} />
        </label>
        {hasPresentations && <div aria-label="Contar por empaques" className="count-lines" role="group">
          {draft.lines.map((line, index) => {
            const presentationName = presentations.find((presentation) => presentation.id === line.presentationId)?.name ?? 'empaque';
            return (
              <div className="count-line" key={line.id ?? index}>
                <label className="field">
                  <span>Empaque</span>
                  <select onChange={(event) => updateLine(index, { presentationId: event.target.value })} value={line.presentationId}>
                    {presentations.map((presentation) => <option key={presentation.id} value={presentation.id}>{presentation.name} ({formatWasteQuantity(presentation.quantityInBaseUnit, unit.symbol)})</option>)}
                  </select>
                </label>
                <label className="field count-line__qty">
                  <span>¿Cuántos?</span>
                  <input inputMode="decimal" maxLength={10} onChange={(event) => updateLine(index, { packages: event.target.value })} placeholder="0" type="text" value={line.packages} />
                </label>
                <button aria-label={`Quitar ${presentationName}`} className="text-button" onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, at) => at !== index) })} type="button">Quitar</button>
              </div>
            );
          })}
          <button className="link-button" onClick={() => setDraft({ ...draft, lines: [...draft.lines, { id: randomUuid(), presentationId: presentations[0]!.id, packages: '' }] })} type="button">+ Contar por empaques (cajas, bolsas…)</button>
        </div>}
        {echo && <p className="wiz-echo wiz-echo--big">{echo}</p>}
        <p className="wiz-hint">Escribe lo que ves en el estante. Después te decimos si cuadra con el sistema.</p>
      </Answers>
    </>;
  }

  const countBlocker = counted.isEmpty ? null : counted.problem;
  // Cada paso tiene su propio botón principal (key distinta): React no reutiliza el mismo nodo con el
  // foco puesto, así un Enter o un doble toque no pasa de «Ver diferencia» a «Confirmar».
  const footer = step === 'DONE' || loadError || !target || !target.storeStarted || target.parallelBalance || (step === 'COUNT' && target.pending?.blocked)
    ? <button className="button button--primary" key="count-close" onClick={close} type="button">{step === 'DONE' ? 'Listo' : 'Cerrar'}</button>
    : step === 'COUNT'
      ? <>
        <FooterReason>{countBlocker}</FooterReason>
        <button className="text-button" disabled={isBusy} key="count-cancel" onClick={close} type="button">Cancelar</button>
        <button className="button button--primary" disabled={isBusy || counted.total === null} key="count-compare" onClick={() => void goCompare()} type="button">{isBusy ? 'Comparando…' : 'Ver diferencia'}</button>
      </>
      : preview?.direction === 'MATCH'
        ? <>
          <button className="button button--ghost" disabled={isBusy} key="count-edit" onClick={() => setStep('COUNT')} type="button">Cambiar lo contado</button>
          <button className="button button--primary" key="count-done" onClick={close} type="button">Listo</button>
        </>
        : <>
          <FooterReason>{compare.blocker}</FooterReason>
          <button className="button button--ghost" disabled={isBusy} key="count-edit" onClick={() => { setStep('COUNT'); setError(undefined); }} type="button">Cambiar lo contado</button>
          <button className="button button--primary" disabled={isBusy || Boolean(compare.blocker)} key="count-confirm" onClick={() => void submit()} type="button">{isBusy ? 'Registrando…' : 'Confirmar conteo'}</button>
        </>;

  return (
    <Modal
      title={`Contar ${balance.ingredientName}`}
      description={`${balance.storeName} · ${step === 'COUNT' ? 'Escribe lo que contaste' : step === 'COMPARE' ? 'Revisa la diferencia' : 'Conteo registrado'}`}
      isBusy={isBusy}
      onClose={close}
      footer={footer}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {body}
    </Modal>
  );
}

function DoneMessage({ result, unitSymbol }: { result: CountResult; unitSymbol: string }) {
  if (result.status === 'matched') return <div className="alert alert--success" role="status"><strong>Cuadra ✓</strong> No se registró nada.</div>;
  const difference = Number(result.difference);
  const magnitude = formatWasteQuantity(String(Math.abs(difference)), unitSymbol);
  const waste = Number(result.detectedWasteQuantity);
  const detail = difference < 0
    ? `faltaban ${magnitude}${waste > 0 ? ` (${formatWasteQuantity(result.detectedWasteQuantity, unitSymbol)} como merma detectada en conteo y ${formatWasteQuantity(result.unexplainedShortage, unitSymbol)} sin explicar)` : ''}`
    : `sobraban ${magnitude}`;
  return <div className="alert alert--success" role="status">
    <strong>Listo.</strong> {result.replayed ? 'Este conteo ya se había registrado y no se duplicó: ' : 'Se registró el conteo: '}
    {detail}. Ahora el sistema dice {formatWasteQuantity(result.countedQuantity, unitSymbol)}.
  </div>;
}
